import React from 'react';
import { LayoutDashboard, FlaskConical, Shield, X } from 'lucide-react';

export type ServiceType = 'member' | 'simulator' | 'admin';

interface ServiceOption {
  type: ServiceType;
  icon: React.ReactNode;
  title: string;
  description: string;
  color: string;
}

interface ServiceSelectionModalProps {
  onSelect: (service: ServiceType) => void;
  availableServices: ServiceType[];
  current?: ServiceType;
  onClose?: () => void;
}

const SERVICE_CONFIG: Record<ServiceType, ServiceOption> = {
  member: {
    type: 'member',
    icon: <LayoutDashboard className="w-7 h-7" />,
    title: 'Espace membre',
    description: 'Accédez à vos données de partage d\'énergie, vos factures et votre consommation.',
    color: '#22C55E',
  },
  simulator: {
    type: 'simulator',
    icon: <FlaskConical className="w-7 h-7" />,
    title: 'Simulateur',
    description: 'Simulez et analysez des scénarios de partage d\'énergie pour votre communauté.',
    color: '#F8BD3D',
  },
  admin: {
    type: 'admin',
    icon: <Shield className="w-7 h-7" />,
    title: 'Espace gestionnaire',
    description: 'Gérez votre communauté énergétique, les membres et les données de partage.',
    color: '#3B82F6',
  },
};

export function ServiceSelectionModal({ onSelect, availableServices, current, onClose }: ServiceSelectionModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <div className="px-6 pt-6 pb-4 border-b border-neutral-100 flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-neutral-900">Choisir un service</h2>
            <p className="text-sm text-neutral-500 mt-1">Vous avez accès à plusieurs services. Lequel souhaitez-vous utiliser&nbsp;?</p>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="text-neutral-400 hover:text-neutral-600 transition-colors ml-4 mt-0.5"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        <div className="p-6 space-y-3">
          {availableServices.map((type) => {
            const svc = SERVICE_CONFIG[type];
            const isActive = current === type;
            return (
              <button
                key={type}
                onClick={() => onSelect(type)}
                className={`w-full flex items-start gap-4 p-4 rounded-xl border-2 text-left transition-all duration-200 group ${
                  isActive
                    ? 'border-current bg-neutral-50'
                    : 'border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50'
                }`}
                style={isActive ? { borderColor: svc.color } : {}}
              >
                <div
                  className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-white transition-all"
                  style={{ backgroundColor: svc.color }}
                >
                  {svc.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-neutral-900 text-base">{svc.title}</span>
                    {isActive && (
                      <span
                        className="text-xs font-medium px-2 py-0.5 rounded-full text-white"
                        style={{ backgroundColor: svc.color }}
                      >
                        Actuel
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-neutral-500 mt-0.5 leading-snug">{svc.description}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
